import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  getBytes,
} from 'firebase/storage';
import { db, storage } from './firebase';
import { Revision, RevisionFiles, ProjectFile } from '../types';
import { generatePdfThumbnail } from '../utils/pdfThumbnail';
import { downloadFileContentFromStorage, isStorageBackedFile } from './projects';

const PROJECTS_COLLECTION = 'projects';
const REVISIONS_COLLECTION = 'revisions';

export async function createRevision(
  projectId: string,
  files: ProjectFile[],
  pdfData: Uint8Array
): Promise<string> {
  const revisionId = crypto.randomUUID();
  const now = new Date();

  // Resolve storage-backed file content before snapshotting
  const resolvedFiles = await Promise.all(
    files.map(async (file) => {
      if (!isStorageBackedFile(file) || !file.storageContentPath) return file;
      const content = await downloadFileContentFromStorage(file.storageContentPath);
      return { ...file, content };
    })
  );

  // Prepare files snapshot (exclude id field)
  const filesSnapshot: RevisionFiles = {
    files: resolvedFiles.map(({ name, content, type, parentId }) => ({
      name,
      content,
      type,
      parentId,
    })),
    capturedAt: now.toISOString(),
  };

  // Upload files.json to Storage
  const filesRef = ref(storage, `projects/${projectId}/revisions/${revisionId}/files.json`);
  const filesBlob = new Blob([JSON.stringify(filesSnapshot)], { type: 'application/json' });
  await uploadBytes(filesRef, filesBlob);

  // Upload PDF to Storage
  const pdfRef = ref(storage, `projects/${projectId}/revisions/${revisionId}/compiled.pdf`);
  await uploadBytes(pdfRef, pdfData, { contentType: 'application/pdf' });

  // Generate and upload thumbnail (for project list display)
  try {
    const thumbnailBlob = await generatePdfThumbnail(pdfData, 300);
    const thumbnailRef = ref(storage, `projects/${projectId}/thumbnail.png`);
    await uploadBytes(thumbnailRef, thumbnailBlob, { contentType: 'image/png' });
  } catch (err) {
    // Thumbnail generation is non-critical, log and continue
    console.warn('Failed to generate thumbnail:', err);
  }

  // Create revision document in Firestore
  const revisionData = {
    createdAt: Timestamp.fromDate(now),
    fileCount: files.filter(f => f.type !== 'folder').length,
    pdfSize: pdfData.byteLength,
    label: null,
  };

  await addDoc(
    collection(db, PROJECTS_COLLECTION, projectId, REVISIONS_COLLECTION),
    { ...revisionData, id: revisionId }
  );

  return revisionId;
}

export async function getRevisions(projectId: string): Promise<Revision[]> {
  const q = query(
    collection(db, PROJECTS_COLLECTION, projectId, REVISIONS_COLLECTION),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.data().id || doc.id,
    createdAt: doc.data().createdAt?.toDate(),
    fileCount: doc.data().fileCount,
    pdfSize: doc.data().pdfSize,
    label: doc.data().label,
  }));
}

export async function getRevisionFiles(
  projectId: string,
  revisionId: string
): Promise<RevisionFiles> {
  const filesRef = ref(storage, `projects/${projectId}/revisions/${revisionId}/files.json`);
  const bytes = await getBytes(filesRef);
  const text = new TextDecoder().decode(bytes);
  return JSON.parse(text);
}

export async function getRevisionPdfUrl(
  projectId: string,
  revisionId: string
): Promise<string> {
  const pdfRef = ref(storage, `projects/${projectId}/revisions/${revisionId}/compiled.pdf`);
  return getDownloadURL(pdfRef);
}

export async function getRevisionPdfData(
  projectId: string,
  revisionId: string
): Promise<Uint8Array> {
  const pdfRef = ref(storage, `projects/${projectId}/revisions/${revisionId}/compiled.pdf`);
  const bytes = await getBytes(pdfRef);
  return new Uint8Array(bytes);
}

export async function updateRevisionLabel(
  projectId: string,
  revisionId: string,
  label: string | null
): Promise<void> {
  // Find the document with matching id field
  const q = query(collection(db, PROJECTS_COLLECTION, projectId, REVISIONS_COLLECTION));
  const snapshot = await getDocs(q);
  const docToUpdate = snapshot.docs.find(d => d.data().id === revisionId);
  if (docToUpdate) {
    await updateDoc(docToUpdate.ref, { label });
  }
}

export async function getProjectThumbnailUrl(
  projectId: string
): Promise<string | null> {
  try {
    const thumbnailRef = ref(storage, `projects/${projectId}/thumbnail.png`);
    return await getDownloadURL(thumbnailRef);
  } catch {
    // Thumbnail doesn't exist yet
    return null;
  }
}

export async function updateProjectThumbnail(
  projectId: string,
  pdfData: Uint8Array
): Promise<void> {
  const thumbnailBlob = await generatePdfThumbnail(pdfData, 300);
  const thumbnailRef = ref(storage, `projects/${projectId}/thumbnail.png`);
  await uploadBytes(thumbnailRef, thumbnailBlob, { contentType: 'image/png' });
}
